import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { getCookie, removeCookie, setCookie } from "cookies/cookie";
import { ResponseNewToken } from "./interfaces/authInterface.interface";
import { getCookieExpireTime } from "utils/timeUtil";
import { setIsAuth } from "redux/reducers/auth/isAuth";
import { notify } from "components/atoms/notification/Notification";
import { store } from "redux/store";
import { getItemWithExpireTime } from "hook/useLocalStorage";
import { checkMaintenance } from "utils/functions";
import { clearAxiosHeaders, setAxiosHeaders } from "utils/AuthUtil";
import { disconnectStomp, reconnectStomp } from "redux/stomp/stompSlice";

export const ACCESS_TOKEN = "access";
export const REFRESH_TOKEN = "refresh";
export const DEVICE_ID = "deviceId";

const API_VERSION = "/v2";

// const authToken = {
//   access: typeof window === 'undefined' && typeof global !== 'undefined' ? null : sessionStorage.getItem('accessToken'),
//   refresh:
//     typeof window === 'undefined' && typeof global !== 'undefined' ? null : sessionStorage.getItem('refreshToken'),
// };

export const redirectToLoginPage = () => {
  window.location.href = `/login`;
};

export const AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}/api${API_VERSION}`,
  timeout: 100000,
});

// 오프라인 에러 알림 표시 여부
let isShowOfflineError = false;

// 온라인 상태 변경 시 오프라인 에러 알림 표시 여부 초기화
window.addEventListener("online", () => {
  isShowOfflineError = false;
});

const clearAuthHeaders = () => {
  clearAxiosHeaders();
  removeCookie(ACCESS_TOKEN);
  removeCookie(REFRESH_TOKEN);
  store.dispatch(setIsAuth(false));
};

AxiosInstance.interceptors.response.use(
  (res: any) => {
    return res;
  },
  async (error) => {
    const err = error as AxiosError;
    if (error.isOffline && !isShowOfflineError) {
      notify("error", error.message);
      isShowOfflineError = true;
    }
    const refreshToken = getCookie(REFRESH_TOKEN);
    if (err.response?.status === 401) {
      if (!refreshToken) {
        console.log("401 error start", "no refresh token");
        store.dispatch(disconnectStomp());
        clearAuthHeaders();
        return;
      }
      console.log("401 error start");
      try {
        const { data, status }: AxiosResponse<ResponseNewToken> =
          await AxiosInstance.post("/auth/accessToken", {
            grantType: "Bearer",
            refreshToken: refreshToken,
          });
        if (status === 200) {
          setCookie({
            name: ACCESS_TOKEN,
            value: data.result.accessToken,
            options: {
              path: "/",
              expires: getCookieExpireTime(6),
              // secure : true,
              // httpOnly : true
            },
          });
          error.config.headers[
            "Authorization"
          ] = `Bearer ${data.result.accessToken}`;
          setAxiosHeaders({
            accessToken: data.result.accessToken,
          });
          //이미 연결되어있을떄만 reconnectStomp 호출
          if (store.getState().stomp.currentUser) {
            store.dispatch(reconnectStomp());
          }

          if (data.result.refreshToken !== undefined) {
            setCookie({
              name: REFRESH_TOKEN,
              value: data.result.refreshToken,
              options: {
                path: "/",
                expires: getCookieExpireTime(72),
                // secure : true,
                // httpOnly : true
              },
            });
          }
          return await axios.request(error.config);
        } else if (status === 599) {
          error.config.headers = {};
          console.log("401 error start", "599");
          window.dataLayer?.push({
            event: 'expired',
            reason: 'access_token_599',
            page: window.location.pathname,
          });
          clearAuthHeaders();
        } else {
          error.config.headers = {};
          console.log("401 error start", "other error");
          window.dataLayer?.push({
            event: 'expired',
            reason: 'access_token_other',
            page: window.location.pathname,
          });
          clearAuthHeaders();
        }
      } catch (err) {
        console.log("401 error start", "catch error");
        window.dataLayer?.push({
          event: 'expired',
          reason: 'access_token_catch',
          page: window.location.pathname,
        });
        store.dispatch(disconnectStomp());
        clearAuthHeaders();
      }
    }
    if (err.response?.status === 404) {
      const maintenance = await checkMaintenance();
      if (maintenance?.isActive) {
        setCookie({
          name: "maintenance",
          value: maintenance.description,
          options: {
            path: "/",
            expires: getCookieExpireTime(1),
          },
        });
        window.location.href = "/maintenance.html";
      }
    }
    return Promise.reject(error);
  }
);

AxiosInstance.interceptors.request.use(async (config: AxiosRequestConfig) => {
  const logoutCookie = await getItemWithExpireTime("lastOrg");
  if (!window.navigator.onLine) {
    console.log(config);
    return Promise.reject({
      message:
        "You're offline. Please reconnect to the internet and try again.",
      isOffline: true,
    });
  }
  if (logoutCookie) {
    localStorage.removeItem("lastOrg");
    try {
      await AxiosInstance.get("/auth/logout/prevent");
    } catch {}
  }
  return config;
});
